import { PaginatedResponse } from '@eagle/api-types';
import { AccumulatorOffset, LastThingEvent } from '@eagle/core-data-types';
import type { EthingsRestClient } from '@eagle/ethings-rest-client';
import { GPS_ODOMETER_FEATURE, ODOMETER_FEATURES, OdometerFeature, OdometerRawReading, OdometerReading, PRIMARY_ODOMETER_FEATURE } from '../types/odometer';

const isOdometerFeature = (feature: string): feature is OdometerFeature =>
  (ODOMETER_FEATURES as string[]).includes(feature);

export const selectOdometerReading = (events: LastThingEvent[]): OdometerRawReading | null => {
  const latestByFeature = new Map<OdometerFeature, LastThingEvent>();

  for (const event of events) {
    if (!isOdometerFeature(event.feature)) continue;
    if (event.eventTypeId !== 'update') continue;

    const value = (event.data as Record<string, unknown> | undefined)?.value;
    if (typeof value !== 'number' || !Number.isFinite(value)) continue;

    const current = latestByFeature.get(event.feature);
    if (!current || event.occurred.getTime() > current.occurred.getTime()) {
      latestByFeature.set(event.feature, event);
    }
  }

  const candidates = [...latestByFeature.values()];
  if (!candidates.length) return null;

  candidates.sort((a, b) => {
    const occurredDifference = b.occurred.getTime() - a.occurred.getTime();
    if (occurredDifference !== 0) return occurredDifference;

    return ODOMETER_FEATURES.indexOf(a.feature as OdometerFeature) - ODOMETER_FEATURES.indexOf(b.feature as OdometerFeature);
  });
  const [latest] = candidates;

  return {
    feature: latest.feature as OdometerFeature,
    rawValueMeters: (latest.data as Record<string, unknown>).value as number,
    occurred: latest.occurred,
  };
};

export const selectApplicableOffset = (offsets: AccumulatorOffset[], asOf: Date): AccumulatorOffset | null => {
  const applicable = offsets.filter((offset) => offset.readingDate.getTime() <= asOf.getTime());
  if (!applicable.length) return null;

  return applicable.reduce((latest, current) => current.readingDate.getTime() > latest.readingDate.getTime() ? current : latest);
};

export const calibrateOdometerValueMeters = (rawValueMeters: number, offset: AccumulatorOffset | null): number => {
  if (!offset) return rawValueMeters;
  const measuredIncrease = rawValueMeters - offset.measuredValue;
  return offset.actualValue + measuredIncrease * (offset.slope ?? 1);
};

const resolveCalibration = (raw: OdometerRawReading, offset: AccumulatorOffset | null): Pick<OdometerReading, 'calibratedValueMeters' | 'appliedOffsetMeters'> => {
  const calibratedValueMeters = calibrateOdometerValueMeters(raw.rawValueMeters, offset);
  if (!offset || calibratedValueMeters < 0) return { calibratedValueMeters: raw.rawValueMeters, appliedOffsetMeters: 0 };

  return { calibratedValueMeters, appliedOffsetMeters: offset.actualValue - offset.measuredValue };
};

export const resolveOdometerReading = (events: LastThingEvent[], offsets: AccumulatorOffset[]): OdometerReading | null => {
  const raw = selectOdometerReading(events);
  if (!raw) return null;

  const offset = raw.feature === GPS_ODOMETER_FEATURE
    ? selectApplicableOffset(offsets, raw.occurred)
    : null;

  return {
    ...raw,
    ...resolveCalibration(raw, offset),
  };
};

const PER_FEATURE_EVENT_LIMIT = 50;

const fetchAccumulatorOffsets = async (restClient: EthingsRestClient, thingId: string, feature: OdometerFeature, asOf: Date): Promise<AccumulatorOffset[]> => {
  try {
    const response = await restClient.accumulatorOffset.getAll(thingId, feature, {
      skip: 0,
      limit: 2,
      dateRangeStart: asOf,
      dateRangeFinish: asOf,
    }) as unknown as PaginatedResponse<AccumulatorOffset>;
    return Array.isArray(response.items) ? response.items : [];
  } catch {
    return [];
  }
};

export const getOdometerReading = async (restClient: EthingsRestClient, thingId: string): Promise<OdometerReading | null> => {
  const responses = await Promise.all(ODOMETER_FEATURES.map((feature) =>
    restClient.lastThingEvent.getWithThing(thingId, {
      filter: {
        feature,
        eventTypeId: 'update',
      },
      sort: '-occurred',
      limit: PER_FEATURE_EVENT_LIMIT,
    }),
  ));

  const raw = selectOdometerReading(responses.flatMap((response) => response.items));
  if (!raw) return null;

  if (raw.feature !== GPS_ODOMETER_FEATURE) {
    return { ...raw, calibratedValueMeters: raw.rawValueMeters, appliedOffsetMeters: 0 };
  }

  let offsets = await fetchAccumulatorOffsets(restClient, thingId, GPS_ODOMETER_FEATURE, raw.occurred);
  if (offsets.length === 0) {
    offsets = await fetchAccumulatorOffsets(restClient, thingId, PRIMARY_ODOMETER_FEATURE, raw.occurred);
  }

  const offset = selectApplicableOffset(offsets, raw.occurred);

  return {
    ...raw,
    ...resolveCalibration(raw, offset),
  };
};
